import { useEffect, useRef, useState } from 'react';

interface InfiniteListProps<T> {
  list: T[];
  children: (item: T, index: number) => React.ReactNode;
}

export function InfiniteList<T>({ list, children }: InfiniteListProps<T>) {
  const [indexLength, setIndexLength] = useState(20); // Initial number of items to render
  const loadMoreTrigger = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // Load more items when the trigger element is intersecting
          setIndexLength((prevIndexLength) => {
            const newIndexLength = prevIndexLength + 20;
            return newIndexLength > list.length ? list.length : newIndexLength;
          }); // Load 20 more items
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 1.0,
      }
    );

    observer.observe(loadMoreTrigger.current);

    return () => {
      // Cleanup the observer when the component unmounts
      observer.disconnect();
    };
  }, [indexLength, list]);

  return (
    <div>
      <ul className="mb-0">
        {list.map((item, index) => index < indexLength && children(item, index))}
      </ul>
      <div ref={loadMoreTrigger} style={{ height: '1px' }} />
    </div>
  );
}
